import { SectionItem } from '@features/shift-post-v2/components'
import { InfoSubsection } from '@src/features/shift-post-v2/steps/shift-details-step/components/position-name-section/components/info-subsection/info-subsection'
import { getPhysicalReqContent } from '@src/features/shift-post-v2/steps/shift-details-step/components/position-name-section/components/info-subsection/info-subsection.utils'
import { EditDetailsModal } from '@src/features/shift-post-v2/steps/shift-details-step/components/position-name-section/modals/edit-details-modal/edit-details-modal'
import { useModal } from '@src/hooks/useModal'
import { useMemo, useState } from 'react'
import { connect } from 'formik'
import { ShiftPostPage } from '@src/features/shift-post-v2/pages'
import { ShiftPostStep } from '@src/features/shift-post-v2/steps/steps.types'
import { FullDescriptionModal } from '@src/features/shift-post-v2/steps/shift-details-step/components/position-name-section/modals/full-description-modal/full-description-modal'
import { Colors, Text } from '@pinchenterprisesnpm/friday-ui'
import { IPositionNameSectionProps } from './position-name-section.types'

export const PositionNameSection = connect<IPositionNameSectionProps>(
  ({ formik, sourcePage, sourceStep }) => {
    const isConfirmStep = sourceStep === ShiftPostStep.CONFIRM
    const [isOpen, toggleModal, closeModal] = useModal()
    const [isFullDescriptionModalOpen, toggleFullDescriptionModal, closeFullDescriptionModal] =
      useModal()
    const [showFullDescription, setShowFullDescription] = useState(false)

    const {
      values: {
        description_rich_text,
        description_short,
        special_instructions_rich_text,
        what_to_wear_rich_text,
        travel_tips_rich_text,
        amount_lifted_frequently,
        amount_lifted_occasionally,
        amount_of_time_spent_on_feet,
        template_id,
        gig_type,
      },
    } = formik

    /**
     * Field editing is disabled if template is not selected.
     */
    const editingDisabled = sourcePage === ShiftPostPage.POST_SHIFT && !template_id

    const infoContent = useMemo(() => {
      return [
        {
          title: 'Description',
          content: description_rich_text,
          required: true,
        },
        {
          title: 'Perks',
          content: description_short,
          required: false,
        },
        {
          title: 'Special Instructions',
          content: special_instructions_rich_text,
          required: false,
        },
        {
          title: 'What to Wear',
          content: what_to_wear_rich_text,
          required: true,
        },
        {
          title: 'Travel Tips',
          content: travel_tips_rich_text,
          required: false,
        },
      ]
    }, [
      description_rich_text,
      special_instructions_rich_text,
      what_to_wear_rich_text,
      travel_tips_rich_text,
      description_short,
    ])

    const showFullDescriptionTitle = showFullDescription ? 'Show less' : 'Show full description'

    const showPhysicalRequirementsCheckmark = useMemo(() => {
      return (
        !!amount_lifted_frequently && !!amount_lifted_occasionally && !!amount_of_time_spent_on_feet
      )
    }, [amount_lifted_frequently, amount_lifted_occasionally, amount_of_time_spent_on_feet])

    const physicalRequirementsStrings = useMemo(() => {
      return [
        {
          title: 'Amount Lifted Frequently',
          content: amount_lifted_frequently ? `${String(amount_lifted_frequently || '')}lbs` : '/',
        },
        {
          title: 'Amount Lifted Occasionally',
          content: amount_lifted_occasionally
            ? `${String(amount_lifted_occasionally || '')}lbs`
            : '/',
        },
        {
          title: 'Amount of Time Spent on Feet',
          content: amount_of_time_spent_on_feet
            ? `${String(amount_of_time_spent_on_feet || '')}%`
            : '/',
        },
      ]
    }, [amount_lifted_frequently, amount_lifted_occasionally, amount_of_time_spent_on_feet])

    const descriptionTitle = useMemo(() => {
      if (isConfirmStep) {
        return ''
      }
      return gig_type ? gig_type : '/'
    }, [gig_type, isConfirmStep])

    const onShowFullDescription = () => {
      if (isConfirmStep) {
        toggleFullDescriptionModal()
      } else {
        setShowFullDescription((prev) => !prev)
      }
    }

    const title = useMemo(() => {
      return isConfirmStep ? 'Position Name' : 'Position Name:'
    }, [isConfirmStep])

    return (
      <SectionItem
        title={title}
        descriptionTitle={descriptionTitle}
        onShowFullDescription={onShowFullDescription}
        showFullDescriptionTitle={showFullDescriptionTitle}
        onEdit={toggleModal}
        hasEdit={true}
        isEditDisabled={editingDisabled}
        editDisabledTooltip='Please select a template to edit the position.'
      >
        {isConfirmStep && (
          <Text fontSize='medium' mt='xs' mb='0' color={Colors.dark}>
            {gig_type ?? '/'}
          </Text>
        )}

        {!isConfirmStep && (
          <>
            {infoContent.map((content) => (
              <InfoSubsection
                key={content.title}
                content={content.content}
                title={content.title}
                showFullDescription={showFullDescription}
                isRequired={content.required}
              />
            ))}

            <InfoSubsection
              title='Physical Requirements'
              content={getPhysicalReqContent(physicalRequirementsStrings)}
              showFullDescription={showFullDescription}
              showCheckmark={showPhysicalRequirementsCheckmark}
            />
          </>
        )}

        <EditDetailsModal isOpen={isOpen} closeModal={closeModal} sourcePage={sourcePage} />

        <FullDescriptionModal
          isOpen={isFullDescriptionModalOpen}
          closeModal={closeFullDescriptionModal}
          infoContent={infoContent}
          physicalRequirementsStrings={physicalRequirementsStrings}
        />
      </SectionItem>
    )
  }
)
